import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ticketCsv } from "@server/reporting/export";
import { statusKind, type StatusKind } from "@server/reporting/lifecycle";
import { isTypeChange, type CrossTab, type ReportTicket } from "@server/reporting/metrics";
import type { IncidentRow } from "@server/reporting/types";
import { ApiError, fetchTicketDetails, useTicketDetails } from "../../api/client";
import { Button, ErrorBanner } from "../../components/ui";

const PAGE_SIZE = 50;
// The details endpoint takes this many ids at once.
const DETAILS_BATCH = 200;
const number = new Intl.NumberFormat("en-GB");

const formatDay = (iso: string | undefined): string =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" }) : "–";

// Links come from what the incident job saved, but only https ones are ever shown as links.
const safeUrl = (url: string | undefined): string | undefined => (url && url.startsWith("https://") ? url : undefined);

export function CrossTabTable({ table, rowHeader, columnHeader }: { table: CrossTab; rowHeader: string; columnHeader: string }) {
  if (table.columns.length === 0) {
    return <p className="record-note">Nothing to show for these filters.</p>;
  }
  return (
    <div className="table-wrap cross-table">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">
              {rowHeader} / {columnHeader}
            </th>
            {table.columns.map((column) => (
              <th key={column} scope="col" className="num">
                {column}
              </th>
            ))}
            <th scope="col" className="num">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {row.cells.map((cell, index) => (
                <td key={table.columns[index]} className="num">
                  {cell === 0 ? "–" : number.format(cell)}
                </td>
              ))}
              <td className="num total">{number.format(row.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Grand total</th>
            {table.totals.map((total, index) => (
              <td key={table.columns[index]} className="num">
                {number.format(total)}
              </td>
            ))}
            <td className="num total">{number.format(table.grandTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const PREVIEW = 160;

// Closed is green, in progress blue, anything waiting on someone amber, and a ticket nobody has picked up yet grey.
const STATUS_TONE: Record<StatusKind, string> = {
  closed: "good",
  "in-progress": "accent",
  "waiting-customer": "warn",
  "waiting-cs": "warn",
  "new-team": "neutral",
  new: "neutral",
  other: "neutral",
};

function StatusChip({ status }: { status: string | undefined }) {
  if (!status) {
    return <span className="record-note">–</span>;
  }
  return <span className={`status-pill status-${STATUS_TONE[statusKind(status)]}`}>{status}</span>;
}

function Details({ text }: { text: string | undefined }) {
  const [open, setOpen] = useState(false);
  if (text === undefined) {
    return <span className="record-note">Loading…</span>;
  }
  if (text.length <= PREVIEW) {
    return <span className="details-text">{text}</span>;
  }
  return (
    <span className="details-text">
      {open ? text : `${text.slice(0, PREVIEW).replace(/\s+/g, " ")}…`}{" "}
      <button type="button" className="link-button" onClick={() => setOpen(!open)}>
        {open ? "Show less" : "Show more"}
      </button>
    </span>
  );
}

function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

// The filtered tickets, newest first, a page at a time. The ticket text is fetched for the visible page only.
export function TicketTable({ rows }: { rows: ReportTicket[] }) {
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const sorted = useMemo(() => [...rows].sort((a, b) => b.raisedAt.localeCompare(a.raisedAt)), [rows]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = sorted.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const details = useTicketDetails(visible.map((row) => row.id));

  async function downloadAll(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const found: Record<string, string> = {};
      for (let start = 0; start < sorted.length; start += DETAILS_BATCH) {
        Object.assign(found, await fetchTicketDetails(sorted.slice(start, start + DETAILS_BATCH).map((row) => row.id)));
      }
      download(`eng-tickets-${new Date().toISOString().slice(0, 10)}.csv`, ticketCsv(sorted, found));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : "The download failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sorted.length === 0) {
    return <p className="record-note">No tickets match these filters.</p>;
  }
  return (
    <div>
      <div className="table-tools">
        <span className="record-note">
          {number.format(current * PAGE_SIZE + 1)}–{number.format(current * PAGE_SIZE + visible.length)} of {number.format(sorted.length)}
        </span>
        <Button variant="secondary" className="btn-sm" disabled={busy} onClick={() => void downloadAll()}>
          {busy ? "Preparing…" : "Download CSV"}
        </Button>
      </div>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <div className="table-wrap">
        <table className="table ticket-table">
          <thead>
            <tr>
              <th scope="col">Raised</th>
              <th scope="col">Raised by</th>
              <th scope="col">Company</th>
              <th scope="col">Status</th>
              <th scope="col">BU</th>
              <th scope="col">Feature</th>
              <th scope="col" className="num">
                Days
              </th>
              <th scope="col">Jira</th>
              <th scope="col">Details</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id}>
                <td>{formatDay(row.raisedAt)}</td>
                <td>{row.raiser || "–"}</td>
                <td>
                  <div className="cell-company">{row.company ?? "–"}</div>
                </td>
                <td>
                  <StatusChip status={row.status} />
                </td>
                <td>{row.businessUnit ?? "–"}</td>
                <td>{row.feature ?? "–"}</td>
                <td className="num">{row.daysToClose === undefined ? "–" : row.daysToClose.toFixed(1)}</td>
                <td>{row.jiraKey ?? "–"}</td>
                <td className="details-cell">
                  <Details text={details.data ? (details.data[row.id] ?? "") : details.isError ? "" : undefined} />
                  {row.overrideReason && (
                    <p className="record-note">
                      <strong>Raised despite a "no ticket" verdict.</strong> Why: {row.overrideReason}
                    </p>
                  )}
                  {isTypeChange(row) && (
                    <p className="record-note">
                      <strong>Type changed</strong> from {row.aiTicketType} to {row.ticketType}.
                    </p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pager">
        <Button variant="secondary" className="btn-sm" disabled={current === 0} onClick={() => setPage(current - 1)}>
          Previous
        </Button>
        <span className="record-note">
          Page {current + 1} of {pages}
        </span>
        <Button variant="secondary" className="btn-sm" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

const INCIDENT_STATUS_LABELS: Record<string, string> = {
  INCIDENT_QUEUED: "Being created",
  INCIDENT_CREATED: "Open",
  INCIDENT_FAILED: "Failed",
  INCIDENT_RESOLVED: "Resolved, follow-up open",
  INCIDENT_COMPLETE: "Complete",
};

export function IncidentTable({ rows }: { rows: IncidentRow[] }) {
  if (rows.length === 0) {
    return <p className="record-note">No incidents yet.</p>;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Raised</th>
            <th scope="col">Title</th>
            <th scope="col">Severity</th>
            <th scope="col">Types</th>
            <th scope="col">Status</th>
            <th scope="col">Resolved</th>
            <th scope="col">Write-up</th>
            <th scope="col">Companies</th>
            <th scope="col">Raised by</th>
            <th scope="col">Jira</th>
            <th scope="col">Channel</th>
          </tr>
        </thead>
        <tbody>
          {[...rows].reverse().map((row) => (
            <tr key={row.id}>
              <td>{formatDay(row.createdAt)}</td>
              <td>
                <Link to={`/incidents/${encodeURIComponent(row.id)}`}>{row.title}</Link>
              </td>
              <td>
                {row.severity}
                {row.aiSeverity && row.aiSeverity !== row.severity && <span className="record-note"> (AI suggested {row.aiSeverity})</span>}
              </td>
              <td>{row.types.join(", ")}</td>
              <td>{INCIDENT_STATUS_LABELS[row.status] ?? row.status}</td>
              <td>{row.resolvedAt ? formatDay(row.resolvedAt) : "–"}</td>
              <td>{safeUrl(row.writeUpUrl) ? <a href={safeUrl(row.writeUpUrl)}>Open</a> : "–"}</td>
              <td>{row.companiesImpacted}</td>
              <td>{row.raiser}</td>
              <td>{safeUrl(row.jiraUrl) ? <a href={safeUrl(row.jiraUrl)}>{row.jiraKey}</a> : "–"}</td>
              <td>{safeUrl(row.channelUrl) ? <a href={safeUrl(row.channelUrl)}>#{row.channelName}</a> : "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
